import { useState, useEffect } from 'react';
import { calculateRoleBasedCosts, RoleBasedCost } from '../utils/roleBasedCostCalculations';

interface TaskProfitability {
  sn: number;
  taskName: string;
  startDate: string;
  endDate: string;
  duration: number;
  numResources: number;
  totalResourceCost: number;
  withBuffer: number;
  profitMargin: number;
  priceToCustomer: number;
  blendedRateToCustomer: number;
}

interface ProjectTaskCostSummary {
  sn: number;
  taskName: string;
  totalResourceCost: number;
  percentageOfTotalCost: number;
}

export const useTaskProfitability = (
  resourceAssignments: any[],
  selectedProject: any,
  profitMargin: number[],
  bufferPercent: number[],
  costAnalysis: any,
  isAllDataFetched: boolean
) => {
  const [taskProfitability, setTaskProfitability] = useState<TaskProfitability[]>([]);
  const [projectTaskCostSummary, setProjectTaskCostSummary] = useState<ProjectTaskCostSummary[]>([]);
  const [calculatedTotalCost, setCalculatedTotalCost] = useState<number>(0);
  const [roleBasedCosts, setRoleBasedCosts] = useState<RoleBasedCost[]>([]);

  const calculateActualTotalCost = () => {
    if (!resourceAssignments.length) return 0;
    
    return resourceAssignments.reduce((total, assignment) => {
      const rate = assignment.rate || 0;
      const duration = assignment.duration || 0;
      const allocationHours = parseFloat(assignment.allocation_hrs) || 0;
      const allocatedPercentage = assignment.allocated_percentage || 100;
      const resourceCost = rate * (allocatedPercentage / 100) * duration * allocationHours;
      
      return total + resourceCost;
    }, 0);
  };

  const calculateTaskProfitability = () => {
    if (!selectedProject?.tasks || !isAllDataFetched) {
      console.log('🔄 COST CALC: Waiting for all data to be fetched before calculating task profitability:', {
        hasProject: !!selectedProject,
        hasTasks: !!selectedProject?.tasks,
        isAllDataFetched,
        resourceAssignmentsCount: resourceAssignments.length
      });
      return;
    }

    // Calculate the actual total cost from resource assignments
    const actualTotalCost = calculateActualTotalCost();
    setCalculatedTotalCost(actualTotalCost);

    console.log('📊 COST CALC: Starting task profitability calculations with complete data:', {
      tasksCount: selectedProject.tasks.length,
      resourceAssignmentsCount: resourceAssignments.length,
      actualTotalCost,
      isAllDataFetched
    });
    
    const taskCosts = new Map<string, {
      totalCost: number;
      startDate: string;
      endDate: string;
      duration: number;
      numResources: number;
      taskName: string;
    }>();
    
    // Group assignments by task and calculate total cost per task
    resourceAssignments.forEach(assignment => {
      const taskId = assignment.task_id;
      const rate = assignment.rate || 0;
      const duration = assignment.duration || 0;
      const allocationHours = parseFloat(assignment.allocation_hrs) || 0;
      const allocatedPercentage = assignment.allocated_percentage || 100;
      const resourceCost = rate * (allocatedPercentage / 100) * duration * allocationHours;
      
      // Use start_date and end_date from resource_assignment table
      const startDate = assignment.start_date || '';
      const endDate = assignment.end_date || '';
      
      if (taskCosts.has(taskId)) {
        const existing = taskCosts.get(taskId)!;
        taskCosts.set(taskId, {
          ...existing,
          totalCost: existing.totalCost + resourceCost,
          numResources: existing.numResources + 1
        });
      } else {
        taskCosts.set(taskId, {
          totalCost: resourceCost,
          startDate: startDate,
          endDate: endDate,
          duration: duration,
          numResources: 1,
          taskName: taskId
        });
      }
    });

    // Convert to profitability data
    const profitabilityData: TaskProfitability[] = Array.from(taskCosts.entries()).map(([taskId, taskData], index) => {
      const marginPercent = profitMargin[0];
      const bufferMultiplier = 1 + (bufferPercent[0] / 100);
      const withBuffer = Math.round(taskData.totalCost * bufferMultiplier);
      const priceToCustomer = withBuffer * (1 + marginPercent / 100);
      
      // Calculate task-level blended rate for individual task display
      // Get resources for this specific task
      const taskResources = resourceAssignments.filter(assignment => assignment.task_id === taskId);
      let taskTotalHours = 0;
      
      taskResources.forEach(assignment => {
        const duration = assignment.duration || 0;
        const allocationHours = parseFloat(assignment.allocation_hrs) || 0;
        taskTotalHours += allocationHours * duration;
      });
      
      const taskBlendedRateToCustomer = taskTotalHours > 0 ? priceToCustomer / taskTotalHours : 0;
      
      return {
        sn: index + 1,
        taskName: taskData.taskName,
        startDate: taskData.startDate,
        endDate: taskData.endDate,
        duration: taskData.duration,
        numResources: taskData.numResources,
        totalResourceCost: taskData.totalCost,
        withBuffer: withBuffer,
        profitMargin: marginPercent,
        priceToCustomer: priceToCustomer,
        blendedRateToCustomer: taskBlendedRateToCustomer
      };
    });

    console.log('✅ COST CALC: Task profitability calculation complete:', {
      totalTasks: profitabilityData.length,
      actualTotalCost,
      sampleData: profitabilityData.slice(0, 2)
    });

    setTaskProfitability(profitabilityData);
  };

  const calculateProjectTaskCostSummary = () => {
    if (!selectedProject?.tasks || !isAllDataFetched) {
      console.log('🔄 COST CALC: Waiting for all data before calculating project task cost summary:', {
        hasProject: !!selectedProject,
        hasTasks: !!selectedProject?.tasks,
        isAllDataFetched,
        resourceAssignmentsCount: resourceAssignments.length
      });
      return;
    }

    // Use the calculated total cost instead of costAnalysis.total_cost
    const actualTotalCost = calculateActualTotalCost();

    console.log('📊 COST CALC: Starting project task cost summary calculations with complete data:', {
      tasksCount: selectedProject.tasks.length,
      resourceAssignmentsCount: resourceAssignments.length,
      actualTotalCost,
      isAllDataFetched
    });
    
    const taskCosts = new Map<string, {
      totalCost: number;
      taskName: string;
    }>();
    
    // Group assignments by task and calculate total cost per task
    resourceAssignments.forEach(assignment => {
      const taskId = assignment.task_id;
      const rate = assignment.rate || 0;
      const duration = assignment.duration || 0;
      const allocationHours = parseFloat(assignment.allocation_hrs) || 0;
      const allocatedPercentage = assignment.allocated_percentage || 100;
      const resourceCost = rate * (allocatedPercentage / 100) * duration * allocationHours;
      
      if (taskCosts.has(taskId)) {
        const existing = taskCosts.get(taskId)!;
        taskCosts.set(taskId, {
          ...existing,
          totalCost: existing.totalCost + resourceCost
        });
      } else {
        taskCosts.set(taskId, {
          totalCost: resourceCost,
          taskName: taskId
        });
      }
    });

    // Convert to project task cost summary data
    const summaryData: ProjectTaskCostSummary[] = Array.from(taskCosts.entries()).map(([taskId, taskData], index) => {
      const percentageOfTotalCost = actualTotalCost > 0 
        ? (taskData.totalCost / actualTotalCost) * 100 
        : 0;
      
      return {
        sn: index + 1,
        taskName: taskData.taskName,
        totalResourceCost: taskData.totalCost,
        percentageOfTotalCost: Number(percentageOfTotalCost.toFixed(1))
      };
    });

    console.log('✅ COST CALC: Project task cost summary calculation complete:', {
      totalTasks: summaryData.length,
      actualTotalCost,
      sampleData: summaryData.slice(0, 2)
    });

    setProjectTaskCostSummary(summaryData);
  };

  const calculateAndSetRoleBasedCosts = () => {
    if (resourceAssignments.length > 0 && isAllDataFetched) {
      console.log('🎯 ROLE CALC: Calculating role-based costs:', {
        resourceAssignmentsCount: resourceAssignments.length,
        isAllDataFetched
      });
      
      const roleBreakdown = calculateRoleBasedCosts(resourceAssignments);
      setRoleBasedCosts(roleBreakdown);
    }
  };

  useEffect(() => {
    if (resourceAssignments.length > 0 && selectedProject?.tasks && isAllDataFetched) {
      console.log('🎯 COST CALC: All conditions met, triggering calculations:', {
        resourceAssignmentsCount: resourceAssignments.length,
        tasksCount: selectedProject.tasks.length,
        isAllDataFetched
      });
      calculateTaskProfitability();
      calculateProjectTaskCostSummary();
      calculateAndSetRoleBasedCosts();
    }
  }, [resourceAssignments, profitMargin, bufferPercent, selectedProject?.tasks, isAllDataFetched]);

  useEffect(() => {
    if (resourceAssignments.length > 0 && selectedProject?.tasks && isAllDataFetched) {
      // Small delay to ensure data is properly calculated
      const timer = setTimeout(() => {
        calculateProjectTaskCostSummary();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [resourceAssignments, selectedProject?.tasks, isAllDataFetched]);

  return {
    taskProfitability,
    projectTaskCostSummary,
    calculatedTotalCost,
    roleBasedCosts
  };
};
